import React from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { 
  useAccounts, 
  useLiabilities, 
  useSpendingBreakdown, 
  useTransactions,
  useNotifications,
  useNetWorthHistory,
  useZecherScore,
  useInsights
} from '../../hooks/useData';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import useIsMobile from '../../hooks/useIsMobile';

import SearchBar from './SearchBar';
import Overview from './Overview';
import Notifications from './Notifications';
import AccountCards from './AccountCards';
import NetWorthSummary from './NetWorthSummary';
import NetWorthChart from './NetWorthChart';
import SpendingBreakdown from './SpendingBreakdown';
import AIChat from './AIChat';
import ZecherScore from './ZecherScore';
import InsightFeed from '../Insights/InsightFeed';
import PlaidStatus from './PlaidStatus';

const Dashboard = () => {
  const isMobile = useIsMobile();
  const { data: accounts, loading: accountsLoading, error: accountsError, refresh: refreshAccounts } = useAccounts();
  const { data: liabilities, loading: liabilitiesLoading, error: liabilitiesError, refresh: refreshLiabilities } = useLiabilities();
  const { data: spendingData, loading: spendingLoading, error: spendingError, refresh: refreshSpending } = useSpendingBreakdown();
  const { data: transactions, loading: transactionsLoading, error: transactionsError, refresh: refreshTransactions } = useTransactions();
  const { data: notifications, loading: notificationsLoading, error: notificationsError, refresh: refreshNotifications } = useNotifications();
  const { data: netWorthHistory, loading: netWorthHistoryLoading, error: netWorthHistoryError, refresh: refreshNetWorthHistory } = useNetWorthHistory();
  const { data: zecherScoreData, loading: zecherScoreLoading, error: zecherScoreError, refresh: refreshZecherScore } = useZecherScore();
  const { data: insights, loading: insightsLoading, error: insightsError, refresh: refreshInsights } = useInsights();

  const loading = accountsLoading || liabilitiesLoading || spendingLoading || transactionsLoading || 
                  notificationsLoading || netWorthHistoryLoading || zecherScoreLoading || insightsLoading;
  const error = accountsError || liabilitiesError || spendingError || transactionsError ||
                notificationsError || netWorthHistoryError || zecherScoreError || insightsError;
  
  const refresh = () => {
    refreshAccounts();
    refreshLiabilities();
    refreshSpending();
    refreshTransactions();
    refreshNotifications();
    refreshNetWorthHistory();
    refreshZecherScore();
    refreshInsights();
  };

  if (loading) return <LoadingState message="Loading dashboard..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!accounts || !liabilities || !spendingData || !transactions || !notifications || !netWorthHistory || !zecherScoreData || !insights) return null;

  // Calculate dynamic values
  const assets = accounts.reduce((sum, acc) => sum + Math.max(0, acc.balance), 0);
  const totalLiabilities = liabilities.reduce((sum, liab) => sum + liab.balance, 0);
  const netWorth = assets - totalLiabilities;

  // Calculate net worth change from history data
  const previousNetWorth = netWorthHistory[netWorthHistory.length - 2]?.value || netWorth;
  const netWorthChange = netWorth - previousNetWorth;
  const netWorthChangePercentage = previousNetWorth !== 0 ? Math.round((netWorthChange / previousNetWorth) * 100) : 0;

  return (
    <View style={styles.container}>
      <ScrollView 
        style={styles.scrollView} 
        contentContainerStyle={[styles.scrollContent, isMobile && styles.mobileScrollContent]}
        showsVerticalScrollIndicator={false}
      >
        {/* Search Bar */}
        <View style={[styles.section, isMobile && styles.mobileSection]}>
          <SearchBar />
        </View>

        {/* Plaid Connection Status */}
        <View style={[styles.section, isMobile && styles.mobileSection]}>
          <PlaidStatus />
        </View>

        {/* Notifications */}
        <View style={[styles.section, isMobile && styles.mobileSection]}>
          <Notifications notifications={notifications} />
        </View>

        {/* Top Section: Overview + Insights */}
        <View style={[styles.section, isMobile && styles.mobileSection]}>
          <View style={styles.overviewSection}>
            <Overview accounts={accounts} />
            <View style={[styles.spacer, isMobile && styles.mobileSpacerSmall]} />
            <InsightFeed insights={insights} />
          </View>
        </View>

        {/* Account Cards */}
        <View style={[styles.section, isMobile && styles.mobileSection]}>
          <AccountCards accounts={accounts} />
        </View>

        {/* Mobile Layout: Stack Everything Vertically */}
        {isMobile ? (
          <>
            {/* Zecher Score */}
            <View style={styles.mobileSection}>
              <ZecherScore score={zecherScoreData.current} />
            </View>

            {/* Net Worth Summary */}
            <View style={styles.mobileSection}>
              <NetWorthSummary data={{
                total: netWorth,
                change: netWorthChange,
                changePercentage: netWorthChangePercentage,
                assets,
                liabilities: Math.abs(totalLiabilities)
              }} />
            </View>

            {/* Spending Breakdown */}
            <View style={styles.mobileSection}>
              <SpendingBreakdown data={spendingData} />
            </View>

            {/* Net Worth Chart */}
            <View style={styles.mobileSection}>
              <NetWorthChart data={netWorthHistory} />
            </View>

            {/* AI Chat */}
            <View style={styles.mobileSection}>
              <AIChat />
            </View>
          </>
        ) : (
          /* Desktop Layout: Two-column bottom section */
          <View style={styles.section}>
            <View style={styles.bottomGrid}>
              {/* Main Charts Area */}
              <View style={styles.chartsSection}>
                <NetWorthSummary data={{
                  total: netWorth,
                  change: netWorthChange,
                  changePercentage: netWorthChangePercentage,
                  assets,
                  liabilities: Math.abs(totalLiabilities)
                }} />
                <View style={styles.spacer} />
                <SpendingBreakdown data={spendingData} />
                <View style={styles.spacer} />
                <NetWorthChart data={netWorthHistory} />
              </View>

              {/* Sidebar */}
              <View style={styles.sidebarSection}>
                <ZecherScore score={zecherScoreData.current} />
                <View style={styles.spacer} />
                <AIChat />
              </View>
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    maxWidth: 1152,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
    paddingBottom: 40,
  },
  mobileScrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingBottom: 32,
  },
  section: {
    marginBottom: 24,
  },
  mobileSection: {
    marginBottom: 20,
  },
  spacer: {
    height: 24,
  },
  mobileSpacerSmall: {
    height: 16,
  },
  overviewSection: {
    flex: 1,
  },
  bottomGrid: {
    flexDirection: 'row',
    gap: 24,
  },
  chartsSection: {
    flex: 2,
    minWidth: 0,
  },
  sidebarSection: {
    flex: 1,
    minWidth: 300,
  },
});

export default Dashboard; 